import { Component, useCallback, useEffect, useState } from 'react'
import Sidebar from './components/Sidebar.jsx'
import { ErrorNote } from './components/ui.jsx'
import Accounts from './pages/Accounts.jsx'
import AccountDetail from './pages/AccountDetail.jsx'
import Intel from './pages/Intel.jsx'
import Triggers from './pages/Triggers.jsx'
import Campaigns, { CampaignDetail } from './pages/Outreach.jsx'
import Prompts from './pages/Prompts.jsx'
import { Appendix, Contacts, Replies, ResearchLog } from './pages/Lists.jsx'
import Summary from './pages/Summary.jsx'
import Tasks from './pages/Tasks.jsx'
import { api } from './lib/api.js'

// One page throwing should not blank the whole app.
class PageBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidUpdate(prev) {
    if (prev.view !== this.props.view && this.state.error) this.setState({ error: null })
  }

  render() {
    if (!this.state.error) return this.props.children
    return (
      <div className="body">
        <div className="err">This screen failed to render: {String(this.state.error.message || this.state.error)}</div>
        <div className="empty">Pick another screen from the left, or reload the page.</div>
      </div>
    )
  }
}

export default function App() {
  const [view, setView] = useState('accounts')
  const [slug, setSlug] = useState(null)
  const [campaignId, setCampaignId] = useState(null)
  const [intelFirm, setIntelFirm] = useState(null)
  const [model, setModel] = useState('claude-opus-5')
  const [models, setModels] = useState([])

  const [stats, setStats] = useState(null)
  const [accounts, setAccounts] = useState([])
  const [intel, setIntel] = useState(null)
  const [segmentDefs, setSegmentDefs] = useState([])
  const [triggers, setTriggers] = useState({ triggers: [], attention: { no_next_step: [], stale: [] } })
  const [campaigns, setCampaigns] = useState([])
  const [outcomes, setOutcomes] = useState({})
  const [taskCounts, setTaskCounts] = useState({ open: 0, overdue: 0 })
  const [due, setDue] = useState([])
  const [error, setError] = useState(null)
  const [me, setMe] = useState(null)

  const load = useCallback(async () => {
    try {
      const [s, a, i, t, c, cfg] = await Promise.all([
        api.stats(),
        api.accounts(),
        api.intel(),
        api.triggers(),
        api.campaigns(),
        api.settings(),
      ])
      setModel(cfg.model)
      setModels(
        Object.entries(cfg.pricing.pricing).map(([id, p]) => ({
          id,
          label: p.label,
          price: `$${p.input} in / $${p.output} out per million tokens`,
        })),
      )
      setStats({ ...s, intel_items: i.items.length, campaigns: (c.campaigns || []).length })
      setAccounts(a)
      setIntel(i)
      setTriggers(t)
      setCampaigns(c.campaigns || [])
      setOutcomes(c.outcomes || {})
      setDue(c.due || [])
      // Nav badge only: a count of what is open and what is late. Best
      // effort, so a tasks problem never blanks the rest of the app.
      api
        .tasks()
        .then((t) => setTaskCounts({ open: (t.week || []).length, overdue: (t.week || []).filter((r) => r.overdue).length }))
        .catch(() => {})
      // Segment labels only, for the accounts filter bar. The Summary page
      // fetches the same route for the full briefs. Best effort: the list
      // must not blank because the segment filter has no labels.
      api
        .segments()
        .then((g) => setSegmentDefs([...g.segments, ...g.overlays, g.suppress].map((x) => ({ key: x.key, label: x.label }))))
        .catch(() => {})
      setError(null)
    } catch (err) {
      setError(err)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Separate from load(): who you are does not change while the app is open,
  // and a failure here must not blank the screen -- the sidebar simply omits
  // the identity block.
  useEffect(() => {
    api.me().then(setMe).catch(() => setMe(null))
  }, [])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      // Close the account first if both are open, since it opens on top.
      setSlug((cur) => {
        if (cur) return null
        setCampaignId(null)
        return cur
      })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // The account opens as a slide-over on top of whatever screen you are on,
  // so the list keeps its filters and scroll position underneath.
  const open = (s) => {
    if (s) setSlug(s)
  }

  // A trigger becomes a campaign: the accounts it caught, seeded with its own
  // headline so the reason to reach out travels with the campaign.
  const buildCampaign = async (trigger) => {
    const created = await api.createCampaign({
      name: trigger.headline.slice(0, 70),
      note: trigger.detail || '',
      account_slugs: (trigger.accounts || []).map((a) => a.slug),
    })
    await load()
    setCampaignId(created.id)
    setView('campaigns')
  }

  const chooseModel = async (id) => {
    setModel(id)
    await api.setSettings({ model: id })
  }

  const replyCount = campaigns.reduce((n, c) => n + (c.stats?.replied || 0), 0)
  const unactioned = triggers.triggers.filter((t) => !t.standing).length
  const researchedAt = stats?.researched_at || ''

  const context = (() => {
    if (view === 'triggers') {
      return (
        <>
          <div className="lbl" style={{ marginBottom: 6 }}>Trigger rules</div>
          {[
            ['Commitment clock', 'var(--warn)'],
            ['Evidence threshold', 'var(--blue)'],
            ['People change', 'var(--violet)'],
            ['Market theme', 'var(--pos)'],
            ['Coverage gap', 'var(--muted)'],
          ].map(([label, colour]) => (
            <div className="sb-ctx-row" key={label}>
              <span style={{ width: 9, height: 9, background: colour, flexShrink: 0 }} />
              {label}
            </div>
          ))}
        </>
      )
    }
    return null
  })()

  return (
    <div className="shell">
      <Sidebar
        view={view}
        go={(v) => {
          setView(v)
          setSlug(null)
          setCampaignId(null)
        }}
        stats={stats}
        unactioned={unactioned}
        replies={replyCount}
        researchedAt={researchedAt}
        me={me}
        openTasks={taskCounts.open}
        overdueTasks={taskCounts.overdue}
      >
        {context}
      </Sidebar>

      <div className="main">
        <PageBoundary view={view}>
        {error ? (
          <div className="body">
            <ErrorNote error={error} />
            <div className="empty">
              The API may not be running. Start it with <code>npm run dev</code> in <code>app/</code>.
            </div>
          </div>
        ) : !stats ? (
          <div className="body"><div className="empty">Loading research…</div></div>
        ) : view === 'tasks' ? (
          <Tasks accounts={accounts} campaigns={campaigns} onOpenAccount={open} />
        ) : view === 'summary' ? (
          <Summary onOpen={open} />
        ) : view === 'accounts' || view === 'wc' || view === 'proc' ? (
          // One table, three entry points. `view` selects a preset -- which
          // columns, which filters, which sort -- not a different component.
          // Keyed on the view so arriving from the nav resets to the preset.
          <Accounts
            key={view}
            view={view}
            accounts={accounts}
            stats={stats}
            models={models}
            model={model}
            onModel={chooseModel}
            onOpen={open}
            onChanged={load}
            segments={segmentDefs}
          />
        ) : view === 'contacts' ? (
          <Contacts accounts={accounts} onOpen={open} onChanged={load} />
        ) : view === 'appendix' ? (
          <Appendix onImported={load} models={models} model={model} onModel={chooseModel} />
        ) : view === 'intel' && intel ? (
          <Intel
            intel={intel}
            areas={stats.areas}
            firm={intelFirm}
            onFirm={setIntelFirm}
            onAdded={load}
            schedule={stats.intel_schedule}
            onCadence={async (cadence) => {
              await api.setIntelSchedule({ cadence })
              load()
            }}
          />
        ) : view === 'triggers' ? (
          <Triggers
            triggers={triggers.triggers}
            attention={triggers.attention}
            onOpen={open}
            onCampaign={buildCampaign}
            onReload={load}
          />
        ) : view === 'campaigns' && campaignId ? (
          <PageBoundary view={campaignId}>
            <CampaignDetail
              campaignId={campaignId}
              accounts={accounts}
              outcomes={outcomes}
              onClose={() => setCampaignId(null)}
              onOpenAccount={open}
              onChanged={load}
            />
          </PageBoundary>
        ) : view === 'campaigns' ? (
          <Campaigns campaigns={campaigns} due={due} accounts={accounts} onOpen={setCampaignId} onReload={load} />
        ) : view === 'prompts' ? (
          <Prompts />
        ) : view === 'log' ? (
          <ResearchLog />
        ) : view === 'replies' ? (
          <Replies onOpen={open} />
        ) : null}
        </PageBoundary>
      </div>

      {slug ? (
        <>
          <div className="scrim" onClick={() => setSlug(null)} />
          <div className="slideover" role="dialog" aria-label="Account detail">
            <PageBoundary view={slug}>
              <AccountDetail slug={slug} onClose={() => setSlug(null)} onChanged={load} />
            </PageBoundary>
          </div>
        </>
      ) : null}
    </div>
  )
}
